import React, { useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeft, Play, Sun, Moon, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useAppStore } from '@/store';
import { useSubscription } from '@/hooks/useSubscription';
import { MapleLeaf } from '@/components/common/MapleLeaf';
import { getNavItems } from '@/lib/navigation';

interface SidebarProps {
  collapsed?: boolean;
  setCollapsed?: (collapsed: boolean) => void;
}

const Sidebar = ({ collapsed = false, setCollapsed }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { profile } = useProfile();
  const { currentTier } = useSubscription();
  const { user: appUser, toggleDarkMode, darkMode } = useAppStore();

  const handleCollapsedChange = useCallback(() => {
    setCollapsed?.(!collapsed);
  }, [collapsed, setCollapsed]);

  const navItems = useMemo(() => {

    return getNavItems(location.pathname, () => undefined).filter((item) => {
      if (item.path === '/access-control' || item.path === '#') {
        return false;
      }
      if (item.adminOnly && profile?.role !== 'admin') {
        return false;
      }
      if (!item.requiredTier) return true;
      return item.requiredTier.includes(currentTier);
    });
  }, [currentTier, location.pathname, profile?.role]);

  const handleOpenDemoVideo = useCallback(() => {
    navigate('/docs');
  }, [navigate]);



  const displayName = profile?.name || authUser?.email?.split('@')[0] || 'User';
  const displayEmail = authUser?.email || '';
  const avatarUrl = profile?.avatar || appUser?.avatar;

  return (

    <div className={`hidden md:flex bg-sidebar text-sidebar-foreground flex-col ${collapsed ? 'w-16' : 'w-64'} transition-all duration-300 border-r border-sidebar-border h-full relative overflow-hidden`}>
      <div className="p-3 flex items-center justify-between flex-shrink-0">
        {!collapsed && (
          <h1 className="text-xl font-bold flex items-center gap-2">
            <MapleLeaf size={24} className="text-canada-red" />
            <span className="bg-gradient-to-r from-canada-red to-canada-red/70 bg-clip-text text-transparent">
              CanXP AI
            </span>
          </h1>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={handleCollapsedChange}
          className="text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <PanelLeft size={20} className={collapsed ? 'rotate-180' : ''} />
        </Button>
      </div>

      <div className="p-3">
        <Button
          className="w-full gap-2 bg-canada-red hover:bg-canada-red/90 text-white"
          onClick={handleOpenDemoVideo}
        >
          <Play size={18} />
          {!collapsed && 'Open Demo Video'}
        </Button>
      </div>

      <ScrollArea className="flex-1 p-3 overflow-x-hidden">
        <div className="space-y-1">
          {navItems.map((item) => (
            <div key={item.path} className="mb-1">
              {item.onClick ? (
                <button
                  onClick={item.onClick}
                  className={`flex items-center gap-2 w-full rounded-md px-3 py-2 transition-colors ${item.active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent/50'}`}
                >
                  {item.icon}
                  {!collapsed && <span>{item.label}</span>}
                </button>
              ) : (
                <Link
                  to={item.path}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 transition-colors ${item.active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent/50'}`}
                >
                  {item.icon}
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="border-t border-sidebar-border py-0.5 px-3">
        <div className="flex items-center gap-3 mb-2">
          {authUser && (
            <>
              {!collapsed && (
                <div className="flex-1 truncate">
                  <p className="font-medium text-sm">{displayName}</p>
                  <p className="text-xs text-sidebar-foreground/60">{displayEmail}</p>
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full hover:bg-sidebar-accent"
                asChild
              >
                <Link to="/profile">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={avatarUrl} alt={displayName} />
                    <AvatarFallback>
                      <Bot size={20} />
                    </AvatarFallback>
                  </Avatar>
                </Link>
              </Button>
            </>
          )}
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toggleDarkMode()}
              className="hover:bg-sidebar-accent"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;